import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import {
    ArrowLeft, Bed, Bath, Maximize2, MapPin, Sparkles, Printer,
    Car, CalendarDays, Share2, CheckCircle2,
    Building2, ChevronLeft, ChevronRight, FileText, X, Eye,
    Users, MessageSquare, Calendar, Link, Copy, Edit3,
    Map as MapIcon, Store, Coffee, GraduationCap, Bus, Cross, TreePine,
    Loader2, Archive, Hash, Phone, RotateCcw, Trash2, CopyPlus, Flag, FileSignature, Send, Globe
} from 'lucide-react';
import { Property } from '../types/types';
import { clsx } from 'clsx';
import { StatusPicker } from '../components/ui/StatusPicker';
import { PropertyDocumentsSection } from '../components/property/PropertyDocumentsSection';
import { PropertyNotesSection } from '../components/property/PropertyNotesSection';
import { MarkdownView } from '../components/ui/MarkdownView';
import { OwnerContractModal } from '../components/ui/OwnerContractModal';
import { PhonePromptModal } from '../components/ui/PhonePromptModal';
import { buildContractUrl } from '../lib/contract-queries';
import { useVitrinaShare } from '../hooks/useVitrinaShare';
import { whatsAppLink, toWhatsAppNumber, telLink } from '../lib/phone';
import { ShareOptionsDialog } from '../components/ui/ShareOptionsDialog';
import { buildIntercambioInquiry, isMobileDeviceUA } from '../lib/intercambio-message-queries';
import { APIProvider, Map as GoogleMap, AdvancedMarker } from '@vis.gl/react-google-maps';
import { MapErrorBoundary } from '../components/ui/MapErrorBoundary';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { shouldShowRenovarPublish } from '../lib/publish-renewal';
import { useDisplayCurrency, useGtqRate, formatPropertyPrice, formatUsd, formatGtq } from '../lib/currency';
import { canViewOwner, canDelete } from '../utils/permissions';
import { humanizeSlug } from '../lib/text';
import { portalLinks, PORTAL_LINK_COLUMNS, type PortalRow } from '../lib/portal-links';

// Pull the YouTube video id out of any common URL shape — youtu.be/X,
// youtube.com/watch?v=X, youtube.com/embed/X, etc. Returns null when the URL
// isn't YouTube so the caller falls back to a plain link.
function parseYoutubeId(url: string | undefined | null): string | null {
    if (!url) return null;
    const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|v\/|shorts\/))([a-zA-Z0-9_-]{11})/);
    return m ? m[1] : null;
}

const PropertyLocationMap = ({ lat, lng }: { lat: string, lng: string }) => {
    const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || '';
    const latNum = parseFloat(lat);
    const lngNum = parseFloat(lng);
    // The call-site only checks truthiness, so a non-numeric string (e.g. a
    // half-entered value, or a legacy "NaN") slips through and parseFloat
    // yields NaN — Google Maps throws on a NaN center during render and trips
    // the top-level ErrorBoundary. Bail to nothing instead. Mirrors the guard
    // in the shared LocationMap and VitrinaPage.
    if (Number.isNaN(latNum) || Number.isNaN(lngNum)) return null;
    const position = { lat: latNum, lng: lngNum };

    return (
        <MapErrorBoundary>
            <APIProvider apiKey={apiKey}>
                <div style={{ height: '100%', width: '100%', minHeight: '350px', borderRadius: '0.75rem', overflow: 'hidden', zIndex: 0 }}>
                    <GoogleMap
                        defaultCenter={position}
                        defaultZoom={15}
                        mapId="PROPERTY_DETAIL_MAP"
                        disableDefaultUI={true}
                        gestureHandling="greedy"
                    >
                        <AdvancedMarker position={position} />
                    </GoogleMap>
                </div>
            </APIProvider>
        </MapErrorBoundary>
    );
};


interface PropertyDetailViewProps {
    property: Property;
    theme: 'light' | 'dark' | 'high-contrast';
    onBack: () => void;
    onViewReport?: () => void;
    onViewMatches?: () => void;
    onEdit?: () => void;
    onPrint?: () => void;
    onStatusChange?: (id: string, newStatus: string) => void;
    onArchive?: () => void;
    onRestore?: () => void;
    onPermanentDelete?: () => void;
    onDuplicate?: () => void;
}

const STATUS_STYLES: Record<string, { bg: string; text: string; dot: string }> = {
    Disponible: { bg: 'bg-emerald-500/15', text: 'text-emerald-400', dot: 'bg-emerald-500' },
    'Con Reserva': { bg: 'bg-amber-500/15', text: 'text-amber-400', dot: 'bg-amber-500' },
    Vendido: { bg: 'bg-rose-500/15', text: 'text-rose-400', dot: 'bg-rose-500' },
    Alquilado: { bg: 'bg-blue-500/15', text: 'text-blue-400', dot: 'bg-blue-500' },
    'Fuera de mercado': { bg: 'bg-slate-500/15', text: 'text-slate-400', dot: 'bg-slate-500' },
};

const OPERATION_LABELS: Record<string, string> = {
    sale: 'Venta',
    rent: 'Renta',
    sale_rent: 'Venta/Renta',
    vacational: 'Vacacional',
    'sale-rent': 'Venta/Renta'
};

// Public-portal "ver en" icon links on a published property. Which portals and
// which URL come from lib/portal-links (live `portals` rows + the portals' own
// gate, same as the app); this only maps a portal to its icon.
const PORTAL_ICONS: Record<string, string> = {
    mapainmueble: '/branding/mapainmueble-icon.png',
    apartamentosguatemala: '/branding/apartamentosguatemala-icon.png',
    casasguatemala: '/branding/casasguatemala-icon.png',
    bodegasenguatemala: '/branding/bodegasenguatemala-icon.png',
    cbrguatemala: '/branding/cbrguatemala-icon.png',
    proyectosbienesraices: '/branding/proyectosbienesraices-icon.png',
    bienesinmuebles: '/branding/bienesinmuebles-icon.svg',
};

// Active portals, fetched once per page load (they change by super-admin UPDATE).
let portalsPromise: Promise<PortalRow[]> | null = null;
function loadPortals(): Promise<PortalRow[]> {
    if (!supabase) return Promise.resolve([]);
    if (!portalsPromise) {
        portalsPromise = Promise.resolve(
            supabase.from('portals').select(PORTAL_LINK_COLUMNS).eq('is_active', true).order('sort_order'),
        ).then(({ data, error }) => {
            if (error) { portalsPromise = null; return []; }
            return (data ?? []) as PortalRow[];
        });
    }
    return portalsPromise;
}

export const PropertyDetailView: React.FC<PropertyDetailViewProps> = ({ property, theme, onBack, onViewReport, onViewMatches, onEdit, onPrint, onStatusChange, onArchive, onRestore, onPermanentDelete, onDuplicate }) => {
    const isLight = theme !== 'dark';
    // Display-currency preference (toggled from the list header) + Banguat rate.
    const [displayCurrency] = useDisplayCurrency();
    const gtqRate = useGtqRate();
    const inGtq = displayCurrency === 'GTQ' && !!gtqRate;
    const navigate = useNavigate();
    const { profile, agency } = useAuth();
    // "Puede eliminar" gates archive / restore / permanent delete (enforced in the DB too).
    const allowDelete = canDelete(profile);
    const [activeGalleryIdx, setActiveGalleryIdx] = useState(0);
    const [lightboxOpen, setLightboxOpen] = useState(false);
    const lightboxTouchX = useRef<number | null>(null);
    const [showShareDialog, setShowShareDialog] = useState(false);
    const [showReportDialog, setShowReportDialog] = useState(false);
    const [hasReported, setHasReported] = useState(false);

    // Scroll the app's main container back to the top whenever this view
    // mounts or switches to a different property. The CRM scrolls inside
    // <main> (AppLayout), not the window, so a stale scrollTop carries
    // over when navigating from a scrolled list (e.g. clicking a card on
    // /intercambio/propiedades at scroll position N lands the detail
    // page at N instead of at the cover photo).
    useEffect(() => {
        const main = document.querySelector('main');
        if (main) main.scrollTo({ top: 0, behavior: 'instant' });
    }, [property?.id]);

    // Owner contact is PII gated by canViewOwner. Fetched separately on the
    // detail screen so list queries don't ship owner contacts to every agent
    // in the agency. Cross-agency Intercambio properties (sharedBy) never
    // surface the owner — the originating agency keeps that contact private.
    const [ownerContact, setOwnerContact] = useState<{ name: string; phone?: string; email?: string } | null>(null);
    const canSeeOwner = canViewOwner(profile, property);
    const isShared = !!(property as any).sharedBy;
    const ownerId = (property as any).owner_id as string | undefined;
    const refreshOwner = useCallback(async () => {
        if (!supabase || !canSeeOwner || isShared || !ownerId) { setOwnerContact(null); return; }
        const { data } = await supabase
            .from('contacts')
            .select('name, phone, email')
            .eq('id', ownerId)
            .maybeSingle();
        if (data) setOwnerContact(data as any);
    }, [canSeeOwner, isShared, ownerId]);
    useEffect(() => { refreshOwner(); }, [refreshOwner]);

    // Sub-Sector slug for the proyectosbienesraices icon. List queries only
    // join sectors(name), so the slug is looked up here, for published
    // listings only. Inactive sectors have no proyecto page.
    const sectorId = (property as any).sector_id as number | undefined;
    const isPublished = property.publish_status === 'published';
    const [sectorSlug, setSectorSlug] = useState<string | null>(null);
    useEffect(() => {
        setSectorSlug(null);
        if (!supabase || !sectorId || !isPublished) return;
        let cancelled = false;
        supabase.from('sectors').select('slug, is_active').eq('id', sectorId).maybeSingle()
            .then(({ data, error }) => {
                if (!cancelled && data && !error && data.is_active && data.slug) setSectorSlug(data.slug);
            });
        return () => { cancelled = true; };
    }, [sectorId, isPublished]);
    const [portals, setPortals] = useState<PortalRow[]>([]);
    useEffect(() => {
        if (!isPublished) return;
        let cancelled = false;
        loadPortals().then(rows => { if (!cancelled) setPortals(rows); });
        return () => { cancelled = true; };
    }, [isPublished]);
    // CBR's portal needs the listing agency's medals: known for our own listings only.
    const ownListing = !!agency && String(property.agency_id) === String(agency.id);
    const listingPortals = portalLinks(
        { ...(property as any), id: String(property.id) },
        portals,
        { agencyMedals: ownListing ? (agency?.medals ?? []) : null, sectorSlug },
    );

    // Latest owner-authorization contract status for this property (Borrador/
    // Firmado/Expirado). Internal CRM info — not shown for cross-agency shares.
    const [contractRow, setContractRow] = useState<{ id: string; status: string; signed_at: string | null } | null>(null);
    const [showSendModal, setShowSendModal] = useState(false);
    const [phonePromptOpen, setPhonePromptOpen] = useState(false);
    const refreshContract = useCallback(async () => {
        if (!supabase || isShared || !property.id) { setContractRow(null); return; }
        const { data } = await supabase
            .from('owner_authorizations')
            .select('id, status, signed_at')
            .eq('property_id', property.id)
            .order('created_at', { ascending: false })
            .limit(1)
            .maybeSingle();
        setContractRow((data as any) ?? null);
    }, [property.id, isShared]);
    useEffect(() => { refreshContract(); }, [refreshContract]);

    // Global marketing tags (property_tags via property_tag_assignments).
    const [propertyTags, setPropertyTags] = useState<{ id: number; name: string; color: string | null }[]>([]);
    useEffect(() => {
        if (!supabase || !property.id) { setPropertyTags([]); return; }
        supabase
            .from('property_tag_assignments')
            .select('property_tags(id, name, color, is_active)')
            .eq('property_id', property.id)
            .then(({ data, error }) => {
                // Deactivated tags stay assigned but are hidden everywhere.
                if (data && !error) setPropertyTags(data.map((r: any) => r.property_tags).filter((t: any) => t && t.is_active !== false));
            });
    }, [property.id]);

    // Build the WhatsApp re-send URL for the existing contract token.
    const buildResendUrl = (phoneRaw: string): string => {
        const url = buildContractUrl(contractRow!.id);
        const message = `Estimado(a) ${ownerContact?.name || ''} en el siguiente enlace se encuentra el formulario de la promoción de la propiedad.\n${url}`;
        return whatsAppLink(phoneRaw, message);
    };
    // "Enviar": no contract → open the create form; borrador → re-send the link
    // (prompting for + saving the owner phone first when it's missing).
    const handleEnviar = () => {
        if (!contractRow) { setShowSendModal(true); return; }
        const phone = ownerContact?.phone?.trim();
        if (!phone) { setPhonePromptOpen(true); return; }
        window.open(buildResendUrl(phone), '_blank', 'noopener');
    };

    // Has the current user already filed an infraction report against this
    // shared property? Drives the Reportar button label ("Reportar" vs
    // "Actualizar reporte"). RLS lets each reporter read their own rows.
    useEffect(() => {
        let cancelled = false;
        if (!supabase || !isShared || !property?.id || !profile?.id) {
            setHasReported(false);
            return;
        }
        supabase
            .from('property_infractions')
            .select('id')
            .eq('property_id', property.id)
            .eq('reporter_profile_id', profile.id)
            .maybeSingle()
            .then(({ data }) => {
                if (!cancelled) setHasReported(!!data);
            });
        return () => { cancelled = true; };
    }, [isShared, property?.id, profile?.id]);

    const handleSubmitReport = async (reason: 'watermark' | 'contact_info' | 'other', details: string) => {
        if (!supabase || !property) return;
        const { error } = await supabase.rpc('report_property_infraction', {
            p_property_id: property.id,
            p_reason: reason,
            p_details: details.trim() || null,
        });
        if (error) {
            alert(`No se pudo enviar el reporte: ${error.message}`);
            return;
        }
        setHasReported(true);
        setShowReportDialog(false);
        alert('Reporte enviado. El equipo de Intercambio lo revisará.');
    };

    const { isCreatingShowcase, handleShareAction } = useVitrinaShare(
        () => [property.id],
        { shareTitle: 'Propiedad', onCopied: () => alert('Vínculo copiado al portapapeles.') },
    );

    const gallery = property.gallery?.length ? property.gallery : [(property.cover_photo_url || property.image)];
    const status = STATUS_STYLES[(property.status || 'Disponible')] ?? STATUS_STYLES['Disponible'];

    // Fullscreen photo viewer: lock background scroll and wire keyboard nav
    // (← / → to move, Esc to close) only while it's open.
    useEffect(() => {
        if (!lightboxOpen) return;
        const onKey = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setLightboxOpen(false);
            else if (e.key === 'ArrowLeft') setActiveGalleryIdx(i => Math.max(0, i - 1));
            else if (e.key === 'ArrowRight') setActiveGalleryIdx(i => Math.min(gallery.length - 1, i + 1));
        };
        window.addEventListener('keydown', onKey);
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            window.removeEventListener('keydown', onKey);
            document.body.style.overflow = prevOverflow;
        };
    }, [lightboxOpen, gallery.length]);

    const panelCls = clsx(
        'rounded-2xl border',
        isLight ? 'bg-white border-slate-100 shadow-sm' : 'bg-white/5 border-white/5'
    );

    const labelCls = 'text-[10px] font-black uppercase tracking-widest text-slate-400';
    const valueCls = clsx('text-sm font-bold', isLight ? 'text-slate-800' : 'text-white');

    return (
        <div className="animate-page-entry relative printable-property">
            <div className="print-margin-table w-full">
                <div className="print-margin-header hidden print-only">
                    <div className="print-margin-cell"></div>
                </div>
                <div className="print-margin-body">
                    {/* Print Only Header */}
                    <div className="hidden print-header">
                        <div>
                            <h1 className="text-3xl font-black text-slate-900 mb-1">Intercambio Inmobiliario</h1>
                            <p className="text-[10px] font-black uppercase tracking-[0.2em] text-indigo-600">Tu Mejor Aliado</p>
                        </div>
                        <div className="text-right">
                            <p className="text-sm font-black text-slate-900">{property.agent?.name || 'Agente Inmobiliario'}</p>
                            <p className="text-[10px] font-bold text-slate-500">{property.agent?.email}</p>
                            <p className="text-[10px] font-bold text-slate-500">{property.agent?.phone}</p>
                        </div>
                    </div>

                    {/* ── Back + Actions bar ── */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 sm:mb-8 print-hidden">
                        <button
                            onClick={onBack}
                            className={clsx(
                                'flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-bold transition-all self-start sm:self-auto',
                                isLight
                                    ? 'bg-white border border-slate-200 text-slate-600 hover:text-slate-900 hover:shadow-sm'
                                    : 'bg-white/5 border border-white/5 text-slate-400 hover:text-white hover:bg-white/10'
                            )}
                        >
                            <ArrowLeft size={16} />
                            Volver al listado
                        </button>

                        <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                            {/* Client Report and Verify Availability — both hidden
                                for Intercambio properties. Reporte Cliente reveals
                                the owner's contact + private fields; Verificar
                                disponibilidad opens a WhatsApp template that goes
                                directly to the owner. Neither belongs to the
                                consuming agency on a cross-agency listing. */}
                            {!property.sharedBy && contractRow && (
                                <span
                                    title="Estado del contrato de autorización del propietario"
                                    className={clsx(
                                        'flex items-center gap-1.5 px-3 py-2.5 rounded-xl text-xs font-bold border whitespace-nowrap',
                                        contractRow.status === 'firmado'
                                            ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                                            : contractRow.status === 'expirado'
                                                ? 'bg-amber-50 text-amber-600 border-amber-200'
                                                : 'bg-slate-100 text-slate-500 border-slate-200'
                                    )}
                                >
                                    <FileSignature size={14} />
                                    {contractRow.status === 'firmado' ? 'Contrato firmado'
                                        : contractRow.status === 'expirado' ? 'Contrato expirado'
                                        : 'Contrato borrador'}
                                </span>
                            )}
                            {!property.sharedBy && onViewReport && (
                                <button
                                    data-tour="reporte-cliente"
                                    onClick={onViewReport}
                                    className={clsx(
                                        'flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-bold transition-all border',
                                        isLight
                                            ? 'bg-indigo-50 border-indigo-100 text-indigo-600 hover:bg-indigo-100'
                                            : 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400 hover:bg-indigo-500/20'
                                    )}
                                >
                                    <FileText size={16} />
                                    Reporte Cliente
                                </button>
                            )}

                            {property.publish_status === 'published'
                                && !(property as any).is_deleted
                                && (property as any).crm_status !== 'archived' && (
                                <>
                                    {listingPortals
                                        .map(l => (
                                            <a
                                                key={l.slug}
                                                href={l.url}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                title={`Ver en ${l.name}`}
                                                className={clsx(
                                                    'flex items-center justify-center w-10 h-10 rounded-xl transition-all border',
                                                    isLight
                                                        ? 'bg-white border-slate-200 hover:border-slate-300 hover:shadow-sm'
                                                        : 'bg-white/5 border-white/5 hover:bg-white/10'
                                                )}
                                            >
                                                {PORTAL_ICONS[l.slug] ? (
                                                    <img
                                                        src={PORTAL_ICONS[l.slug]}
                                                        alt={l.name}
                                                        className="w-6 h-6 object-contain"
                                                    />
                                                ) : (
                                                    <Globe size={18} className={isLight ? 'text-slate-500' : 'text-slate-300'} />
                                                )}
                                            </a>
                                        ))}
                                </>
                            )}

                            {/* Verificar disponibilidad — only inside the renewal
                                window (same condition as the Renovar button: published
                                & within 7 days of expiry, or already expired), and only
                                when the owner is assigned with a phone. Sends the owner a
                                renewal-token link where they choose Renovar or Sacar del
                                mercado. ownerContact is PII-gated by canViewOwner. */}
                            {!property.sharedBy
                                && shouldShowRenovarPublish(property)
                                && ownerContact?.phone && (
                                <button
                                    onClick={async () => {
                                        try {
                                            const { data, error } = await supabase!.rpc('create_renewal_token', { p_property_id: property.id });
                                            if (error || !data) throw new Error(error?.message || 'error');
                                            const link = `https://intercambioinmobiliario.com/renovar/${data.token_id}`;
                                            const message = `Estimado(a) ${data.owner_name || ''}, de parte de ${data.agent_name || ''}.\n\nLa promoción de su propiedad "${data.property_title || ''}" está por vencer. Puede renovarla o retirarla del mercado en el siguiente enlace:\n${link}`;
                                            window.open(whatsAppLink(data.owner_phone || ownerContact?.phone, message), '_blank');
                                        } catch {
                                            alert('No se pudo generar el enlace de renovación.');
                                        }
                                    }}
                                    className={clsx(
                                        'flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-xs font-bold transition-all border whitespace-nowrap',
                                        isLight
                                            ? 'bg-emerald-50 border-emerald-100 text-emerald-600 hover:bg-emerald-100'
                                            : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20'
                                    )}
                                >
                                    <MessageSquare size={16} />
                                    <span className="hidden sm:inline">Verificar disponibilidad</span>
                                    <span className="sm:hidden">Verificar</span>
                                </button>
                            )}

                            {/* Share via WhatsApp (Vitrina) */}
                            <button
                                onClick={() => setShowShareDialog(true)}
                                disabled={isCreatingShowcase}
                                className={clsx(
                                    'flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-xs font-bold transition-all border whitespace-nowrap',
                                    isLight
                                        ? 'bg-green-50 border-green-100 text-green-600 hover:bg-green-100'
                                        : 'bg-green-500/10 border-green-500/20 text-green-400 hover:bg-green-500/20',
                                    isCreatingShowcase && 'opacity-50 cursor-not-allowed'
                                )}
                            >
                                {isCreatingShowcase ? <Loader2 size={16} className="animate-spin" /> : <Share2 size={16} />}
                                <span className="hidden sm:inline">Compartir por WhatsApp</span>
                                <span className="sm:hidden">Compartir</span>
                            </button>

                            {/* Status badge / Picker — hidden for Intercambio
                                properties since the status belongs to the
                                originating agency and is not the caller's to
                                change. */}
                            {onStatusChange && (
                                <div className="hidden sm:block">
                                    <StatusPicker
                                        propertyId={property.id}
                                        currentStatus={property.status || 'Disponible'}
                                        onStatusChange={onStatusChange}
                                        align="left"
                                        variant="large"
                                    />
                                </div>
                            )}

                            {/* Matches badge */}
                            {property.shareStatus === 'Compartida' && property.matchesCount !== undefined && (
                                <button
                                    onClick={onViewMatches}
                                    className={clsx(
                                        "hidden md:flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-black border transition-all",
                                        isLight
                                            ? "bg-purple-50 text-purple-600 border-purple-200 hover:bg-purple-100 hover:shadow-md"
                                            : "bg-purple-500/20 text-purple-400 border-purple-500/30 hover:bg-purple-500/30 shadow-lg shadow-purple-500/10"
                                    )}
                                >
                                    <Users size={14} className="text-purple-400" />
                                    <span>{property.matchesCount} Coincidencias</span>
                                </button>
                            )}
                            {onEdit && (
                                <button
                                    onClick={onEdit}
                                    className={clsx(
                                        'p-2.5 rounded-xl border transition-all',
                                        isLight
                                            ? 'bg-white border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-300'
                                            : 'bg-white/5 border-white/5 text-slate-400 hover:text-white hover:bg-white/10'
                                    )}
                                    title="Editar Propiedad"
                                >
                                    <Edit3 size={18} />
                                </button>
                            )}
                            {onDuplicate && !(property as any).is_deleted && (
                                <button
                                    onClick={onDuplicate}
                                    className={clsx(
                                        'p-2.5 rounded-xl border transition-all',
                                        isLight
                                            ? 'bg-white border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-300'
                                            : 'bg-white/5 border-white/5 text-slate-400 hover:text-indigo-400 hover:bg-white/10'
                                    )}
                                    title="Duplicar propiedad"
                                >
                                    <CopyPlus size={18} />
                                </button>
                            )}
                            {/* Archive lifecycle. "Archived" includes both V2 archives (is_deleted=true)
                                and V1 legacy imports (crm_status='archived', often without is_deleted). */}
                            {allowDelete && onArchive && !(property as any).is_deleted && (property as any).crm_status !== 'archived' && (
                                <button
                                    onClick={onArchive}
                                    className={clsx(
                                        'p-2.5 rounded-xl border transition-all',
                                        isLight
                                            ? 'bg-white border-slate-200 text-slate-400 hover:text-amber-600 hover:border-amber-300'
                                            : 'bg-white/5 border-white/5 text-slate-400 hover:text-amber-400 hover:bg-white/10'
                                    )}
                                    title="Archivar propiedad"
                                >
                                    <Archive size={18} />
                                </button>
                            )}
                            {allowDelete && onRestore && ((property as any).is_deleted || (property as any).crm_status === 'archived') && (
                                <button
                                    onClick={onRestore}
                                    className={clsx(
                                        'flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-xs font-bold transition-all border whitespace-nowrap',
                                        isLight
                                            ? 'bg-emerald-50 border-emerald-100 text-emerald-600 hover:bg-emerald-100'
                                            : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20'
                                    )}
                                >
                                    <RotateCcw size={16} />
                                    <span>Restaurar</span>
                                </button>
                            )}
                            {allowDelete && onPermanentDelete && ((property as any).is_deleted || (property as any).crm_status === 'archived') && (
                                <button
                                    onClick={onPermanentDelete}
                                    className={clsx(
                                        'flex items-center gap-2 px-3 sm:px-4 py-2.5 rounded-xl text-xs font-bold transition-all border whitespace-nowrap',
                                        isLight
                                            ? 'bg-rose-50 border-rose-100 text-rose-600 hover:bg-rose-100'
                                            : 'bg-rose-500/10 border-rose-500/20 text-rose-400 hover:bg-rose-500/20'
                                    )}
                                >
                                    <Trash2 size={16} />
                                    <span className="hidden sm:inline">Eliminar permanentemente</span>
                                    <span className="sm:hidden">Eliminar</span>
                                </button>
                            )}
                            <button
                                onClick={() => onPrint ? onPrint() : window.print()}
                                className={clsx(
                                    'p-2.5 rounded-xl border transition-all',
                                    isLight
                                        ? 'bg-white border-slate-200 text-slate-400 hover:text-indigo-600 hover:border-indigo-300'
                                        : 'bg-white/5 border-white/5 text-slate-400 hover:text-white hover:bg-white/10'
                                )}
                                title="Ficha Imprimible"
                            >
                                <Printer size={18} />
                            </button>
                        </div>
                    </div>

                    {/* Archived / deleted banner — surfaces non-active state since the
                        StatusPicker only conveys availability, not lifecycle. Common after
                        migration where state=5 legacy rows land with crm_status='archived'. */}
                    {((property as any).is_deleted || (property as any).crm_status === 'archived') && (
                        <div className={clsx(
                            'mb-6 p-3 rounded-xl border flex items-center gap-2 text-sm print-hidden',
                            'bg-amber-500/10 border-amber-500/30 text-amber-700 dark:text-amber-400'
                        )}>
                            <Archive size={16} />
                            <span className="font-semibold">
                                Esta propiedad está archivada — no aparece en los listados ni en los portales públicos. Puedes restaurarla o eliminarla permanentemente.
                            </span>
                        </div>
                    )}

                    <div className="grid print-layout-grid grid-cols-1 xl:grid-cols-3 gap-8">

                        {/* ── LEFT COLUMN ── */}
                        <div className="xl:col-span-2 space-y-6 min-w-0">

                            {/* Hero image + gallery */}
                            <div className={clsx('stagger-item rounded-3xl overflow-hidden', panelCls)} style={{ '--i': 1 } as React.CSSProperties}>
                                {/* Main image — object-contain so portrait AND landscape
                                    photos show in full (no cropping). A blurred, cropped
                                    copy fills the letterbox area so the bars read as
                                    intentional ambient framing rather than empty gaps. */}
                                <div className="relative aspect-[16/9] overflow-hidden bg-slate-950">
                                    <img
                                        src={gallery[activeGalleryIdx]}
                                        alt=""
                                        aria-hidden="true"
                                        className="absolute inset-0 w-full h-full object-cover scale-110 blur-2xl opacity-50"
                                    />
                                    <img
                                        src={gallery[activeGalleryIdx]}
                                        alt={property.title}
                                        onClick={() => setLightboxOpen(true)}
                                        className="relative w-full h-full object-contain transition-all duration-500 cursor-zoom-in"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-slate-950/60 via-transparent to-transparent pointer-events-none" />

                                    {/* Fullscreen trigger */}
                                    <button
                                        onClick={() => setLightboxOpen(true)}
                                        aria-label="Ver en pantalla completa"
                                        title="Ver en pantalla completa"
                                        className="absolute top-5 right-5 p-2 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-all"
                                    >
                                        <Maximize2 size={18} />
                                    </button>

                                    {/* Premium badge */}
                                    {(property.price ?? 0) > 1000000 && (
                                        <span className="absolute top-5 left-5 flex items-center gap-1.5 px-3 py-1.5 rounded-chip text-[10px] font-bold uppercase tracking-wider text-white"
                                            style={{ backgroundColor: 'var(--accent-600)', border: '1px solid var(--accent-400)' }}>
                                            <Sparkles size={11} /> Premium
                                        </span>
                                    )}

                                    {/* Gallery nav */}
                                    {gallery.length > 1 && (
                                        <>
                                            <button
                                                onClick={() => setActiveGalleryIdx(i => Math.max(0, i - 1))}
                                                className="chevron-left absolute left-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-all disabled:opacity-30"
                                                disabled={activeGalleryIdx === 0}
                                            >
                                                <ChevronLeft size={20} />
                                            </button>
                                            <button
                                                onClick={() => setActiveGalleryIdx(i => Math.min(gallery.length - 1, i + 1))}
                                                className="chevron-right absolute right-4 top-1/2 -translate-y-1/2 p-2 rounded-full bg-black/40 backdrop-blur-sm text-white hover:bg-black/60 transition-all disabled:opacity-30"
                                                disabled={activeGalleryIdx === gallery.length - 1}
                                            >
                                                <ChevronRight size={20} />
                                            </button>
                                            {/* Dots */}
                                            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-1.5">
                                                {gallery.map((_, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => setActiveGalleryIdx(i)}
                                                        className={clsx('w-1.5 h-1.5 rounded-full transition-all', i === activeGalleryIdx ? 'w-4 bg-white' : 'bg-white/50')}
                                                    />
                                                ))}
                                            </div>
                                        </>
                                    )}

                                    {/* (Location moved below price per request) */}
                                </div>

                                {/* Thumbnail strip */}
                                {gallery.length > 1 && (
                                    <div className="flex gap-3 p-4 overflow-x-auto no-scrollbar">
                                        {gallery.map((img, i) => (
                                            <button
                                                key={i}
                                                onClick={() => setActiveGalleryIdx(i)}
                                                className={clsx(
                                                    'shrink-0 w-20 h-14 rounded-xl overflow-hidden border-2 transition-all',
                                                    i === activeGalleryIdx
                                                        ? 'scale-105'
                                                        : isLight ? 'border-transparent opacity-60 hover:opacity-90' : 'border-transparent opacity-40 hover:opacity-70'
                                                )}
                                                style={i === activeGalleryIdx ? { borderColor: 'var(--accent-500)' } : {}}
                                            >
                                                <img src={img} alt="" className="w-full h-full object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* Title + quick specs */}
                            <div className={clsx('stagger-item p-6 space-y-4', panelCls)} style={{ '--i': 2 } as React.CSSProperties}>
                                <div className="flex items-start justify-between gap-4 min-w-0">
                                    <div className="min-w-0 flex-1">
                                        <h1 className={clsx('text-2xl font-black tracking-tight mb-2', isLight ? 'text-slate-900' : 'text-white')}>
                                            {property.title}
                                        </h1>
                                        <div className="flex flex-col gap-1">
                                            {(property.ii_id || property.sharedBy) && (
                                                <div className="mb-1">
                                                    <span
                                                        className={clsx(
                                                            'inline-flex items-center gap-1 text-[11px] font-black font-mono tracking-wider px-2 py-0.5 rounded-md border',
                                                            isLight ? 'bg-slate-50 text-slate-500 border-slate-200' : 'bg-white/5 text-slate-300 border-white/10'
                                                        )}
                                                        title={property.ii_id ? `${property.ii_id} · ${property.id}` : String(property.id)}
                                                    >
                                                        <Hash size={11} />
                                                        {property.ii_id
                                                            ? property.ii_id.toUpperCase()
                                                            : String(property.id).toUpperCase().slice(0, 8)}
                                                    </span>
                                                </div>
                                            )}
                                            {(property.property_type_name || property.property_type || 'Propiedad') && (
                                                <div className="mb-1">
                                                    <span className={clsx('text-xs font-black px-2.5 py-1 rounded-lg border uppercase tracking-widest', isLight ? 'bg-indigo-50 text-indigo-500 border-indigo-100/50' : 'bg-white/5 text-indigo-300 border-white/10 shadow-lg shadow-indigo-500/5')}>
                                                        {(property.property_type_name || property.property_type || 'Propiedad')}
                                                        {property.operation && ` en ${OPERATION_LABELS[property.operation] || property.operation}`}
                                                    </span>
                                                </div>
                                            )}
                                            <div className="flex items-center gap-3 flex-wrap">
                                                <div className="flex items-baseline gap-2 flex-wrap">
                                                    {/* Original-price strikethrough — surfaces price reductions
                                                        carried over from legacy (1k+ migrated rows) and any V2-native
                                                        row where price_reduced was flagged. */}
                                                    {property.price_reduced && property.original_price && Number(property.original_price) > (Number(property.price) || 0) && (
                                                        <span className={clsx('text-base font-bold line-through', isLight ? 'text-slate-400' : 'text-slate-500')}>
                                                            {inGtq ? formatGtq(Number(property.original_price) * gtqRate!) : formatUsd(Number(property.original_price))}
                                                        </span>
                                                    )}
                                                    <p className={clsx('text-2xl font-black', isLight ? 'text-indigo-600' : 'text-indigo-400')}>
                                                        {formatPropertyPrice(property, displayCurrency, gtqRate)}
                                                    </p>
                                                    {property.currency === 'GTQ' && (
                                                        <span title="Precio fijado en quetzales" className="px-2 py-0.5 rounded-lg bg-sky-500/10 text-sky-500 text-[9px] font-black uppercase tracking-wider border border-sky-500/20">
                                                            Precio colocado en Q
                                                        </span>
                                                    )}
                                                    {property.price_reduced && property.original_price && Number(property.original_price) > (Number(property.price) || 0) && (
                                                        <span className="px-2 py-0.5 rounded-lg bg-rose-500/10 text-rose-500 text-[9px] font-black uppercase tracking-wider border border-rose-500/20">
                                                            Bajó {Math.round(((Number(property.original_price) - (Number(property.price) || 0)) / Number(property.original_price)) * 100)}%
                                                        </span>
                                                    )}
                                                    {property.price_label && (
                                                        <span className="px-2 py-0.5 rounded-lg bg-amber-500/10 text-amber-500 text-[9px] font-black uppercase tracking-wider border border-amber-500/20">
                                                            {property.price_label}
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Mantenimiento / IUSI — shown inline below the price when set. ~12% of
                                                migrated rows carry a maintenance amount; iusi is rare but supported. */}
                                            {((property.maintenance != null && Number(property.maintenance) > 0) || ((property as any).iusi != null && Number((property as any).iusi) > 0)) && (
                                                <div className="flex flex-wrap gap-3 mt-1 px-1 text-xs text-slate-500">
                                                    {property.maintenance != null && Number(property.maintenance) > 0 && (
                                                        <span>
                                                            Mantenimiento: Q{Number(property.maintenance).toLocaleString('en-US')}
                                                            {property.maintenance_included && <span className="ml-1 text-emerald-500 font-bold">(incluido)</span>}
                                                        </span>
                                                    )}
                                                    {(property as any).iusi != null && Number((property as any).iusi) > 0 && (
                                                        <span>IUSI: Q{Number((property as any).iusi).toLocaleString('en-US')}</span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Unit price calculation */}
                                            {(() => {
                                                const pType = (property.property_type_name || property.property_type || '').toLowerCase();
                                                const isLot = pType.includes('terreno') || pType.includes('finca');
                                                const areaValue = isLot ? property.lot_size_varas : property.built_up_area;
                                                const unitName = isLot ? 'v²' : 'm²';

                                                if (!areaValue || areaValue <= 0) return null;

                                                return (
                                                    <div className={clsx('flex items-center gap-2 px-1 text-sm font-bold mt-1 transition-colors', isLight ? 'text-slate-400' : 'text-slate-500')}>
                                                        <div className="w-1.5 h-3.5 rounded-full bg-indigo-500/30" />
                                                        ${Math.round(property.price / areaValue).toLocaleString('en-US')} / {unitName}
                                                    </div>
                                                );
                                            })()}
                                            <div className="flex items-start gap-2 mt-2 px-1">
                                                <div className="p-1 rounded-lg bg-slate-100/50 dark:bg-white/5 mt-0.5">
                                                    <MapPin size={15} style={{ color: 'var(--accent-400)' }} />
                                                </div>
                                                <div>
                                                    <p className={clsx('text-base font-bold', isLight ? 'text-slate-600' : 'text-slate-300')}>
                                                        {Array.from(new Set([property.sector_name, property.city_name || 'Ciudad', property.department_name || 'Guatemala'].filter(Boolean))).join(', ')}
                                                    </p>
                                                    {/* Street address — not always populated for migrated rows but
                                                        useful when present. Kept on a separate, smaller line so the
                                                        primary location label still leads. */}
                                                    {property.address && (
                                                        <p className="text-xs text-slate-500 mt-0.5">{property.address}</p>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Spec pills — "+" suffix surfaces "5+ habitaciones" cases legacy stored
                                    via bedrooms_plus / bathrooms_plus / parking_plus. Lot pill replaces "Año"
                                    when the property has a lot area but no year_built (typical for terrenos). */}
                                <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 pt-2">
                                    {(() => {
                                        const bedSuffix = property.bedrooms_plus ? '+' : '';
                                        const bathSuffix = property.bathrooms_plus ? '+' : '';
                                        const parkSuffix = property.parking_plus ? '+' : '';
                                        const lotValue = property.lot_size_varas
                                            ? `${Number(property.lot_size_varas).toLocaleString('en-US')} v²`
                                            : null;
                                        const fifthPill = (property as any).year_built
                                            ? { icon: CalendarDays, label: 'Año', value: String((property as any).year_built) }
                                            : lotValue
                                                ? { icon: Maximize2, label: 'Lote', value: lotValue }
                                                : { icon: CalendarDays, label: 'Año', value: '—' };
                                        return [
                                            { icon: Bed, label: 'Habitaciones', value: `${(property.bedrooms || 0)}${bedSuffix}` },
                                            { icon: Bath, label: 'Baños', value: `${(property.bathrooms || 0)}${bathSuffix}` },
                                            { icon: Maximize2, label: 'Área', value: `${(property.built_up_area || 0)} m²` },
                                            { icon: Car, label: 'Parking', value: property.parking ? `${property.parking}${parkSuffix}` : '—' },
                                            fifthPill,
                                        ];
                                    })().map(({ icon: Icon, label, value }) => (
                                        <div key={label} className={clsx(
                                            'flex flex-col items-center p-3 rounded-2xl gap-2',
                                            isLight ? 'bg-slate-50' : 'bg-white/5'
                                        )}>
                                            <div className="p-2 rounded-xl" style={{ backgroundColor: 'var(--accent-10)' }}>
                                                <Icon size={16} style={{ color: 'var(--accent-400)' }} />
                                            </div>
                                            <p className={clsx('text-base font-black', isLight ? 'text-slate-900' : 'text-white')}>{value}</p>
                                            <p className="text-[9px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Description */}
                            {property.description && (
                                <div className={clsx('stagger-item p-6 space-y-3', panelCls)} style={{ '--i': 3 } as React.CSSProperties}>
                                    <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>Descripción</h3>
                                    <div className={clsx('text-sm leading-relaxed whitespace-pre-wrap break-words', isLight ? 'text-slate-600' : 'text-slate-300')}>
                                        {property.description}
                                    </div>
                                </div>
                            )}

                            {/* Private agent notes — visible to: super_admin, the assigned agent,
                                same-agency agency_admin, and same-agency agents granted the
                                "Ver todas las propiedades" permission (can_view_all_properties).
                                Hidden when viewing a property shared from another agency
                                (sharedBy === true) so the receiving side never sees the owning
                                agent's internal notes. */}
                            {property.agent_notes && !isShared && (
                                profile?.role === 'super_admin' ||
                                profile?.id === property.agent_id ||
                                (profile?.role === 'agency_admin' && profile?.agency_id === property.agency_id) ||
                                (profile?.agency_id === property.agency_id && profile?.permissions?.can_view_all_properties === true)
                            ) && (
                                <div
                                    className={clsx(
                                        'stagger-item p-6 space-y-3 rounded-2xl border',
                                        isLight ? 'bg-amber-50/60 border-amber-200/70' : 'bg-amber-500/5 border-amber-500/20'
                                    )}
                                    style={{ '--i': 3.5 } as React.CSSProperties}
                                >
                                    <div className="flex items-center justify-between gap-3 flex-wrap">
                                        <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-amber-800' : 'text-amber-300')}>Notas Privadas</h3>
                                        <span className={clsx('text-[10px] font-bold uppercase tracking-widest', isLight ? 'text-amber-700' : 'text-amber-400')}>Solo agencia</span>
                                    </div>
                                    <MarkdownView
                                        value={property.agent_notes}
                                        className={clsx('text-sm leading-relaxed break-words', isLight ? 'text-amber-900' : 'text-amber-100')}
                                    />
                                </div>
                            )}

                            {/* Bitácora de notas — dated note log (private + owner-report).
                                Same viewer gate as Notas Privadas (assigned agent / same-agency
                                admin / super_admin / same-agency view-all agent); hidden
                                cross-agency. Add limited to assigned agent / same-agency admin /
                                super_admin; edit/delete to the note's author or an admin. The
                                owner-report notes also render on the Reporte Cliente. */}
                            {!isShared && (
                                profile?.role === 'super_admin' ||
                                profile?.id === property.agent_id ||
                                (profile?.role === 'agency_admin' && profile?.agency_id === property.agency_id) ||
                                (profile?.agency_id === property.agency_id && profile?.permissions?.can_view_all_properties === true)
                            ) && (
                                <div className="stagger-item" style={{ '--i': 3.7 } as React.CSSProperties}>
                                    <PropertyNotesSection
                                        propertyId={String(property.id)}
                                        theme={theme}
                                        currentUserId={profile?.id ?? null}
                                        canWrite={
                                            profile?.role === 'super_admin'
                                            || profile?.id === property.agent_id
                                            || (profile?.role === 'agency_admin' && profile?.agency_id === property.agency_id)
                                        }
                                        canManageOthers={
                                            profile?.role === 'super_admin'
                                            || (profile?.role === 'agency_admin' && profile?.agency_id === property.agency_id)
                                        }
                                    />
                                </div>
                            )}

                            {/* Etiquetas — global marketing tags */}
                            {propertyTags.length > 0 && (
                                <div className={clsx('p-6 space-y-4', panelCls)}>
                                    <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>Etiquetas</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {propertyTags.map(tag => (
                                            <span
                                                key={tag.id}
                                                className="px-3 py-1.5 rounded-chip text-xs font-bold text-white"
                                                style={{ backgroundColor: tag.color || '#6366f1' }}
                                            >
                                                {tag.name}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Amenities */}
                            {property.amenities && property.amenities.length > 0 && (
                                <div className={clsx('p-6 space-y-4', panelCls)}>
                                    <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>Amenidades</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {property.amenities.map(amenity => (
                                            <span
                                                key={amenity}
                                                className="flex items-center gap-1.5 px-3 py-1.5 rounded-chip text-xs font-bold border transition-all"
                                                style={{
                                                    backgroundColor: 'var(--accent-10)',
                                                    borderColor: 'var(--accent-20)',
                                                    color: 'var(--accent-400)'
                                                }}
                                            >
                                                <CheckCircle2 size={11} />
                                                {amenity}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Servicios — legacy stored a JSON array of slug-style keys
                                (e.g. "casa_club_piscina", "aire_acondicionado"). 65k migrated
                                rows have data here. Render as humanised chips so they're
                                legible without a separate translation table. */}
                            {Array.isArray(property.services) && property.services.length > 0 && (
                                <div className={clsx('p-6 space-y-4', panelCls)}>
                                    <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>Servicios</h3>
                                    <div className="flex flex-wrap gap-2">
                                        {property.services.map(svc => (
                                            <span
                                                key={svc}
                                                className={clsx(
                                                    'flex items-center gap-1.5 px-3 py-1.5 rounded-chip text-xs font-bold border',
                                                    isLight ? 'bg-slate-50 border-slate-200 text-slate-700' : 'bg-white/5 border-white/10 text-slate-300'
                                                )}
                                            >
                                                <CheckCircle2 size={11} />
                                                {humanizeSlug(svc)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Video — supports YouTube embed when the URL is recognisable,
                                otherwise renders a link out. Rare in migrated data (~37 rows)
                                but the column exists and a few V2-native rows use it.
                                Never on Intercambio listings: video is not shared across agencies
                                (get_shared_properties returns it NULL; this is the UI backstop). */}
                            {property.video_url && !isShared && (
                                <div className={clsx('p-6 space-y-3', panelCls)}>
                                    <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>Video</h3>
                                    {(() => {
                                        const ytId = parseYoutubeId(property.video_url);
                                        if (ytId) {
                                            return (
                                                <div className="aspect-video rounded-xl overflow-hidden bg-black">
                                                    <iframe
                                                        src={`https://www.youtube.com/embed/${ytId}`}
                                                        title="Video"
                                                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                                        allowFullScreen
                                                        className="w-full h-full"
                                                    />
                                                </div>
                                            );
                                        }
                                        return (
                                            <a href={property.video_url} target="_blank" rel="noreferrer" className="text-sm text-indigo-500 hover:underline break-all">
                                                {property.video_url}
                                            </a>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>

                        {/* ── RIGHT COLUMN ── */}
                        <div className="space-y-6 min-w-0">

                            {/* Stats card — hidden for Intercambio properties since
                                the metrics belong to the originating agency. */}
                            {!property.sharedBy && (
                            <div className={clsx('stagger-item print-hidden p-6 rounded-2xl border', isLight ? 'bg-white border-slate-100 shadow-sm' : 'bg-white/5 border-white/5')} style={{ '--i': 4 } as React.CSSProperties}>
                                <h3 className={clsx('text-sm font-black uppercase tracking-widest mb-5', isLight ? 'text-slate-700' : 'text-white')}>
                                    Métricas de la Propiedad
                                </h3>
                                
                                <div className="space-y-4">
                                    {/* Visits */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-xl bg-indigo-50 dark:bg-indigo-500/10">
                                                <Eye size={18} className="text-indigo-500 dark:text-indigo-400" />
                                            </div>
                                            <p className={clsx('text-sm font-bold', isLight ? 'text-slate-600' : 'text-slate-300')}>Visitas</p>
                                        </div>
                                        <p className={clsx('text-lg font-black', isLight ? 'text-slate-900' : 'text-white')}>
                                            {property.page_visits || 0}
                                        </p>
                                    </div>

                                    {/* Contacts */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-xl bg-amber-50 dark:bg-amber-500/10">
                                                <MessageSquare size={18} className="text-amber-500 dark:text-amber-400" />
                                            </div>
                                            <p className={clsx('text-sm font-bold', isLight ? 'text-slate-600' : 'text-slate-300')}>Contactos</p>
                                        </div>
                                        <p className={clsx('text-lg font-black', isLight ? 'text-slate-900' : 'text-white')}>
                                            {property.contact_count || 0}
                                        </p>
                                    </div>

                                    {/* Visits */}
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <div className="p-2 rounded-xl bg-emerald-50 dark:bg-emerald-500/10">
                                                <Calendar size={18} className="text-emerald-500 dark:text-emerald-400" />
                                            </div>
                                            <p className={clsx('text-sm font-bold', isLight ? 'text-slate-600' : 'text-slate-300')}>Visitas Presenciales</p>
                                        </div>
                                        <p className={clsx('text-lg font-black', isLight ? 'text-slate-900' : 'text-white')}>
                                            {property.visit_count || 0}
                                        </p>
                                    </div>
                                </div>
                            </div>
                            )}

                            {/* Location card */}
                            <div className={clsx('stagger-item p-6', panelCls)} style={{ '--i': 5 } as React.CSSProperties}>
                                <h3 className={clsx('text-sm font-black uppercase tracking-widest mb-4', isLight ? 'text-slate-700' : 'text-white')}>Ubicación</h3>

                                <div className="flex items-center gap-2 mb-6">
                                    <MapPin size={16} style={{ color: 'var(--accent-400)' }} />
                                    <p className={clsx('text-sm font-semibold', isLight ? 'text-slate-700' : 'text-slate-200')}>
                                        {Array.from(new Set([property.sector_name, property.city_name || 'Ciudad', property.department_name || 'Guatemala'].filter(Boolean))).join(', ')}
                                    </p>
                                </div>

                                <div className="flex flex-col print:flex-row gap-8">
                                    {/* Left: Lugares Cercanos */}
                                    <div className="flex-1">
                                        <h4 className={clsx('text-xs font-black uppercase tracking-wider mb-4', isLight ? 'text-slate-500' : 'text-slate-400')}>Lugares de Interés</h4>
                                        <div className="space-y-3">
                                            {property.points_of_interest && property.points_of_interest.length > 0 ? (
                                                property.points_of_interest.map((place, idx) => {
                                                    const getIcon = (name: string) => {
                                                        const lower = name.toLowerCase();
                                                        if (lower.includes('centro') || lower.includes('mall') || lower.includes('super') || lower.includes('comercial')) return <Store size={14} className="text-blue-500" />;
                                                        if (lower.includes('colegio') || lower.includes('escuela') || lower.includes('universidad')) return <GraduationCap size={14} className="text-orange-500" />;
                                                        if (lower.includes('parque')) return <TreePine size={14} className="text-emerald-500" />;
                                                        if (lower.includes('estación') || lower.includes('bus') || lower.includes('metro')) return <Bus size={14} className="text-indigo-500" />;
                                                        if (lower.includes('hospital') || lower.includes('clinica')) return <Cross size={14} className="text-rose-500" />;
                                                        return <MapPin size={14} className="text-slate-400" />;
                                                    };
                                                    return (
                                                        <div key={idx} className="flex items-center justify-between group">
                                                            <div className="flex items-center gap-3">
                                                                <div className={clsx('p-2 rounded-lg transition-colors', isLight ? 'bg-slate-50 group-hover:bg-slate-100' : 'bg-white/5 group-hover:bg-white/10')}>
                                                                    {getIcon(place.name)}
                                                                </div>
                                                                <p className={clsx('text-sm font-semibold transition-colors', isLight ? 'text-slate-700 group-hover:text-slate-900' : 'text-slate-300 group-hover:text-white')}>
                                                                    {place.name}
                                                                </p>
                                                            </div>
                                                            <span className={clsx('text-xs font-bold px-2 py-1 rounded-md transition-colors', isLight ? 'bg-slate-50 text-slate-500 group-hover:bg-slate-100' : 'bg-white/5 text-slate-400 group-hover:bg-white/10')}>
                                                                {place.description}
                                                            </span>
                                                        </div>
                                                    );
                                                })
                                            ) : (
                                                <p className={clsx('text-sm italic', isLight ? 'text-slate-500' : 'text-slate-400')}>
                                                    No hay lugares de interés registrados.
                                                </p>
                                            )}
                                        </div>
                                    </div>

                                    {/* Right: Map — only when the property opted into a
                                        public location ("Mostrar al público" =
                                        show_exact_location). Off → the map column is
                                        omitted so it doesn't waste space. */}
                                    {(property as any).show_exact_location && (
                                    <div className="flex-1">
                                        {property.latitude && property.longitude ? (
                                            <div className="w-full h-full min-h-[350px] rounded-xl flex items-center justify-center bg-slate-100 dark:bg-white/5 relative z-0">
                                                <PropertyLocationMap lat={property.latitude} lng={property.longitude} />
                                            </div>
                                        ) : (
                                            <div className={clsx(
                                                'w-full h-full min-h-[350px] rounded-xl flex items-center justify-center',
                                                isLight ? 'bg-slate-100' : 'bg-white/5'
                                            )}>
                                                <div className="text-center">
                                                    <MapIcon size={28} className="mx-auto mb-1 opacity-20" />
                                                    <p className="text-xs text-slate-500">Mapa interactivo</p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                    )}
                                </div>
                            </div>

                            {/* Agent card */}
                            {(property.agent_name || property.agent) && (
                                <div className={clsx('p-6 space-y-4', panelCls)}>
                                    <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>Agente Responsable</h3>
                                    {(() => {
                                        const agentProfileId = property.agent_id || (property.agent as any)?.id;
                                        const agentInner = (
                                            <>
                                                <div className="w-14 h-14 rounded-2xl p-[1.5px] shrink-0" style={{ background: 'linear-gradient(135deg, var(--accent-500), var(--accent-400))' }}>
                                                    <div className={clsx('w-full h-full rounded-[13px] overflow-hidden flex items-center justify-center', isLight ? 'bg-slate-100' : 'bg-slate-900')}>
                                                        {(property.agent_photo || (property.agent as any)?.avatar) ? (
                                                            <img src={property.agent_photo || (property.agent as any)?.avatar} alt="" className="w-full h-full object-cover" />
                                                        ) : (
                                                            <span className="text-lg font-black" style={{ color: 'var(--accent-500)' }}>
                                                                {(property.agent_name || (property.agent as any)?.name || 'A')[0].toUpperCase()}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                                <div>
                                                    <p className={clsx('font-bold text-sm transition-colors', isLight ? 'text-slate-900' : 'text-white', agentProfileId && 'group-hover:text-indigo-500')}>
                                                        {property.agent_name || (property.agent as any)?.name || 'Agente'}
                                                    </p>
                                                    <p className="text-xs text-slate-400">Asesor Inmobiliario</p>
                                                </div>
                                            </>
                                        );

                                        return agentProfileId ? (
                                            <button
                                                type="button"
                                                onClick={() => navigate(`/colegas/${agentProfileId}`)}
                                                className="group flex items-center gap-4 w-full text-left rounded-xl -m-2 p-2 transition-colors hover:bg-slate-50 dark:hover:bg-white/5 cursor-pointer"
                                                title="Ver perfil del agente"
                                            >
                                                {agentInner}
                                            </button>
                                        ) : (
                                            <div className="flex items-center gap-4">
                                                {agentInner}
                                            </div>
                                        );
                                    })()}

                                    {/* Cross-agency contact actions (Intercambio only). The
                                        consuming agent doesn't share an inbox / WhatsApp with
                                        the originating agent's own-agency teammates, so this
                                        is the only contact path available to them. */}
                                    {property.sharedBy && (() => {
                                        const agentId = (property.agent as any)?.id;
                                        const phoneRaw = (property.agent as any)?.phone as string | undefined;
                                        const hasPhone = (phoneRaw ? phoneRaw.replace(/\D/g, '') : '').length >= 8;
                                        const inquiry = buildIntercambioInquiry(property.title, property.ii_id);
                                        // WhatsApp leaves the CRM, so it carries the deep link; the
                                        // in-app composer doesn't — its header shows the property.
                                        const waInquiry = buildIntercambioInquiry(property.title, property.ii_id, String(property.id));
                                        const messageHref = agentId
                                            ? `/intercambio/mensajes?peer=${agentId}&property=${property.id}&draft=${encodeURIComponent(inquiry)}`
                                            : null;
                                        // WhatsApp only when the number confidently normalizes — see phone.ts.
                                        const waHref = toWhatsAppNumber(phoneRaw) ? whatsAppLink(phoneRaw, waInquiry) : null;
                                        // Llamar (tel:) is mobile-only — on desktop the
                                        // link can't initiate a call so the button is
                                        // hidden to avoid dead UI.
                                        const telHref = hasPhone && isMobileDeviceUA() ? telLink(phoneRaw) : null;

                                        if (!messageHref && !waHref && !telHref) return null;

                                        return (
                                            <div className="flex flex-wrap gap-2 pt-2">
                                                {messageHref && (
                                                    <button
                                                        onClick={() => navigate(messageHref)}
                                                        className={clsx(
                                                            'flex-1 min-w-[100px] flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all border',
                                                            isLight
                                                                ? 'bg-indigo-50 border-indigo-100 text-indigo-600 hover:bg-indigo-100'
                                                                : 'bg-indigo-500/10 border-indigo-500/20 text-indigo-400 hover:bg-indigo-500/20'
                                                        )}
                                                    >
                                                        <MessageSquare size={14} />
                                                        Mensaje
                                                    </button>
                                                )}
                                                {waHref && (
                                                    <a
                                                        href={waHref}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        className={clsx(
                                                            'flex-1 min-w-[100px] flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all border',
                                                            isLight
                                                                ? 'bg-green-50 border-green-100 text-green-600 hover:bg-green-100'
                                                                : 'bg-green-500/10 border-green-500/20 text-green-400 hover:bg-green-500/20'
                                                        )}
                                                    >
                                                        <Share2 size={14} />
                                                        WhatsApp
                                                    </a>
                                                )}
                                                {telHref && (
                                                    <a
                                                        href={telHref}
                                                        className={clsx(
                                                            'flex-1 min-w-[100px] flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-all border',
                                                            isLight
                                                                ? 'bg-emerald-50 border-emerald-100 text-emerald-600 hover:bg-emerald-100'
                                                                : 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/20'
                                                        )}
                                                    >
                                                        <Phone size={14} />
                                                        Llamar
                                                    </a>
                                                )}
                                            </div>
                                        );
                                    })()}

                                </div>
                            )}

                            {/* Reportar Propiedad Compartida — Intercambio-only,
                                its own block so the affordance is discoverable
                                independent of the agent card and signals that
                                reporting is about the LISTING (watermarks, leaked
                                contact info, etc.), not the agent. */}
                            {property.sharedBy && (
                                <div className={clsx('p-6 space-y-3', panelCls)}>
                                    <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>
                                        Reportar Propiedad Compartida
                                    </h3>
                                    <p className={clsx('text-xs leading-relaxed', isLight ? 'text-slate-500' : 'text-slate-400')}>
                                        Avísanos si esta publicación incumple las reglas de Intercambio (sello de agua en fotos, datos de contacto en fotos o descripción, etc.). El equipo lo revisará.
                                    </p>
                                    <button
                                        type="button"
                                        onClick={() => setShowReportDialog(true)}
                                        className={clsx(
                                            'flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-xs font-bold transition-colors w-full border',
                                            isLight
                                                ? 'border-rose-200 bg-rose-50 text-rose-600 hover:bg-rose-100'
                                                : 'border-rose-500/30 bg-rose-500/10 text-rose-400 hover:bg-rose-500/20'
                                        )}
                                        title={hasReported ? 'Actualizar tu reporte sobre esta propiedad' : 'Reportar a Intercambio'}
                                    >
                                        <Flag size={14} />
                                        {hasReported ? 'Actualizar reporte' : 'Reportar'}
                                    </button>
                                </div>
                            )}

                            {/* Owner card — never shown for Intercambio
                                properties; the owner contact is PII belonging to
                                the originating agency. Same-agency: the owner
                                contact is gated by canViewOwner (assigned agent
                                + can_view_all_owners + admins). When hidden, we
                                still surface the section so the agent knows an
                                owner exists but the data is restricted. */}
                            {!isShared && (
                                <div className={clsx('p-6 space-y-4', panelCls)}>
                                    <h3 className={clsx('text-sm font-black uppercase tracking-widest', isLight ? 'text-slate-700' : 'text-white')}>Propietario</h3>
                                    {/* Owner: unassigned → "Sin asignar"; assigned + visible → contact;
                                        assigned + restricted → notice; assigned + loading → nothing. */}
                                    {!ownerId ? (
                                        <p className={clsx('text-sm font-semibold italic', isLight ? 'text-slate-400' : 'text-slate-500')}>Sin asignar</p>
                                    ) : canSeeOwner && ownerContact ? (
                                        <div className="flex items-center gap-4">
                                            <div className={clsx('w-12 h-12 rounded-xl flex items-center justify-center text-sm font-black', isLight ? 'bg-amber-50 text-amber-600' : 'bg-amber-500/10 text-amber-400')}>
                                                {ownerContact.name?.[0]?.toUpperCase() || 'P'}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className={clsx('font-bold text-sm truncate', isLight ? 'text-slate-900' : 'text-white')}>{ownerContact.name}</p>
                                                {ownerContact.phone && <p className="text-xs text-slate-400">{ownerContact.phone}</p>}
                                                {ownerContact.email && <p className="text-xs text-slate-400 truncate">{ownerContact.email}</p>}
                                            </div>
                                        </div>
                                    ) : !canSeeOwner ? (
                                        <p className={clsx('text-xs italic', isLight ? 'text-slate-500' : 'text-slate-400')}>
                                            Información del propietario restringida. Solicita acceso al administrador de la agencia.
                                        </p>
                                    ) : null}

                                    {/* Contrato — always shown (own-agency, visible owner). Main region
                                        opens the doc when one exists; "Enviar" sends/re-sends via
                                        WhatsApp (hidden once firmado). */}
                                    {canSeeOwner && (() => {
                                        const status = contractRow?.status;
                                        const showEnviar = status !== 'firmado'; // sin firmar or borrador
                                        const statusLabel = !contractRow
                                            ? 'Sin firmar'
                                            : status === 'firmado'
                                                ? `Firmado${contractRow.signed_at ? ' · ' + new Date(contractRow.signed_at).toLocaleDateString('es-GT') : ''}`
                                                : status === 'expirado' ? 'Expirado' : 'Pendiente de firma';
                                        const Main = (
                                            <>
                                                <div className={clsx('w-9 h-9 rounded-lg flex items-center justify-center shrink-0',
                                                    status === 'firmado'
                                                        ? (isLight ? 'bg-emerald-50 text-emerald-600' : 'bg-emerald-500/10 text-emerald-400')
                                                        : (isLight ? 'bg-slate-100 text-slate-500' : 'bg-white/10 text-slate-300'))}>
                                                    <FileSignature size={16} />
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className={clsx('font-bold text-sm', isLight ? 'text-slate-900' : 'text-white')}>Contrato</p>
                                                    <p className={clsx('text-xs text-slate-400', !contractRow && 'italic')}>{statusLabel}</p>
                                                </div>
                                            </>
                                        );
                                        return (
                                            <div data-tour="contrato" className={clsx('flex items-stretch rounded-xl border overflow-hidden', isLight ? 'bg-slate-50 border-slate-200' : 'bg-white/5 border-white/10')}>
                                                {contractRow ? (
                                                    <button
                                                        onClick={() => window.open('/formulario/' + contractRow.id, '_blank', 'noopener')}
                                                        title="Ver contrato de autorización"
                                                        className={clsx('flex-1 min-w-0 flex items-center gap-3 p-3 text-left transition-all', isLight ? 'hover:bg-slate-100' : 'hover:bg-white/10')}
                                                    >
                                                        {Main}
                                                        {!showEnviar && <ChevronRight size={18} className="text-slate-400 shrink-0" />}
                                                    </button>
                                                ) : (
                                                    <div className="flex-1 min-w-0 flex items-center gap-3 p-3">{Main}</div>
                                                )}
                                                {showEnviar && (
                                                    <button
                                                        onClick={handleEnviar}
                                                        title="Enviar contrato por WhatsApp"
                                                        className={clsx('flex items-center gap-1.5 px-4 border-l text-xs font-bold transition-all', isLight ? 'border-slate-200 text-emerald-600 hover:bg-emerald-50' : 'border-white/10 text-emerald-400 hover:bg-emerald-500/10')}
                                                    >
                                                        <Send size={15} /> Enviar
                                                    </button>
                                                )}
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}

                            {/* Send-contract form (no contract yet) + phone prompt (re-send w/o phone). */}
                            {showSendModal && ownerId && (
                                <OwnerContractModal
                                    ownerId={ownerId}
                                    ownerName={ownerContact?.name || ''}
                                    ownerPhone={ownerContact?.phone}
                                    properties={[property as any]}
                                    onClose={() => setShowSendModal(false)}
                                    onCreated={() => { refreshContract(); refreshOwner(); }}
                                />
                            )}
                            {phonePromptOpen && ownerId && (
                                <PhonePromptModal
                                    ownerId={ownerId}
                                    ownerName={ownerContact?.name}
                                    onClose={() => setPhonePromptOpen(false)}
                                    onSaved={(savedPhone) => {
                                        setOwnerContact((prev) => prev ? { ...prev, phone: savedPhone } : prev);
                                        if (contractRow) window.open(buildResendUrl(savedPhone), '_blank', 'noopener');
                                    }}
                                />
                            )}

                            {/* Documentos — list + upload + download. Read RLS allows the
                                assigned agent / same-agency agency_admin / super_admin / same-agency
                                agent with can_view_all_properties. Write RLS stays narrower (no
                                view-all branch), so we pass canWrite to hide Upload/Delete from
                                view-all agents who only have read access. Hidden entirely when
                                viewing a property shared from another agency (isShared) — documents
                                are private to the owning agency. */}
                            {!isShared && (
                                <PropertyDocumentsSection
                                    propertyId={String(property.id)}
                                    theme={theme}
                                    canWrite={
                                        profile?.role === 'super_admin'
                                        || profile?.id === property.agent_id
                                        || (profile?.role === 'agency_admin' && profile?.agency_id === property.agency_id)
                                    }
                                />
                            )}

                            {/* Additional Photos for Print ONLY */}
                            {gallery.length > 1 && (
                                <div className={clsx('hidden print-gallery', panelCls)}>
                                    <h3 className={clsx('text-sm font-black uppercase tracking-widest mb-4', isLight ? 'text-slate-700' : 'text-white')}>Galería de Fotos</h3>
                                    <div className="grid grid-cols-2 gap-4 print-gallery-grid">
                                        {gallery.slice(1, 5).map((img, i) => (
                                            <div key={i} className="aspect-[4/3] rounded-xl overflow-hidden shadow-sm border border-slate-200 print-gallery-img-container">
                                                <img src={img} alt={`Galería ${i + 1}`} className="w-full h-full object-cover" />
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                        </div>
                    </div>

                    {/* ── Print Only Footer ── */}
                    <div className="hidden print-footer mt-12 pt-8 border-t border-slate-100 flex justify-between items-center">
                        <div className="text-[10px] text-slate-400">
                            © 2026 Intercambio Inmobiliario • Tu Mejor Aliado • www.intercambioinmobiliario.com
                        </div>
                        <div className="flex items-center gap-3">
                            <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest text-right">
                                Escanea para ver<br />la propiedad online
                            </p>
                            <div className="w-12 h-12 bg-white border border-slate-200 rounded-lg p-1.5 shrink-0 flex items-center justify-center">
                                {/* Mock QR Code Pattern */}
                                <div className="w-full h-full grid grid-cols-4 gap-0.5 opacity-40">
                                    {[...Array(16)].map((_, i) => (
                                        <div key={i} className={clsx("rounded-[1px]", (i % 3 === 0 || i % 7 === 0) ? "bg-slate-900" : "bg-transparent")} />
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>{/* End print-margin-body */}
                <div className="print-margin-footer hidden print-only">
                    <div className="print-margin-cell"></div>
                </div>
            </div> {/* End print-margin-table */}

            {/* Fullscreen photo viewer. Portaled to <body> for the same reason
                as the dialogs below — the page wrapper's residual transform
                would otherwise reframe this fixed overlay off-screen. */}
            {lightboxOpen && typeof document !== 'undefined' && createPortal(
                <div
                    className="fixed inset-0 z-[100] bg-black/95 flex flex-col select-none"
                    onClick={() => setLightboxOpen(false)}
                >
                    {/* Top bar: counter + close */}
                    <div className="flex items-center justify-between px-5 py-4 text-white/90" onClick={e => e.stopPropagation()}>
                        <span className="text-sm font-semibold tabular-nums">
                            {activeGalleryIdx + 1} / {gallery.length}
                        </span>
                        <button
                            onClick={() => setLightboxOpen(false)}
                            aria-label="Cerrar"
                            className="p-2 rounded-full bg-white/10 hover:bg-white/20 transition-all"
                        >
                            <X size={22} />
                        </button>
                    </div>

                    {/* Image fits the viewport; click the backdrop (not the image) closes.
                        Horizontal swipe navigates on touch devices. */}
                    <div
                        className="flex-1 min-h-0 flex items-center justify-center px-4"
                        onTouchStart={e => { lightboxTouchX.current = e.touches[0].clientX; }}
                        onTouchEnd={e => {
                            if (lightboxTouchX.current === null) return;
                            const dx = e.changedTouches[0].clientX - lightboxTouchX.current;
                            lightboxTouchX.current = null;
                            if (dx > 50) setActiveGalleryIdx(i => Math.max(0, i - 1));
                            else if (dx < -50) setActiveGalleryIdx(i => Math.min(gallery.length - 1, i + 1));
                        }}
                    >
                        <img
                            src={gallery[activeGalleryIdx]}
                            alt={property.title}
                            onClick={e => e.stopPropagation()}
                            className="max-w-full max-h-full object-contain"
                        />
                    </div>

                    {gallery.length > 1 && (
                        <>
                            <button
                                onClick={e => { e.stopPropagation(); setActiveGalleryIdx(i => Math.max(0, i - 1)); }}
                                disabled={activeGalleryIdx === 0}
                                aria-label="Anterior"
                                className="absolute left-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all disabled:opacity-25"
                            >
                                <ChevronLeft size={26} />
                            </button>
                            <button
                                onClick={e => { e.stopPropagation(); setActiveGalleryIdx(i => Math.min(gallery.length - 1, i + 1)); }}
                                disabled={activeGalleryIdx === gallery.length - 1}
                                aria-label="Siguiente"
                                className="absolute right-4 top-1/2 -translate-y-1/2 p-3 rounded-full bg-white/10 text-white hover:bg-white/20 transition-all disabled:opacity-25"
                            >
                                <ChevronRight size={26} />
                            </button>

                            {/* Thumbnail strip */}
                            <div className="flex gap-2 p-4 overflow-x-auto no-scrollbar justify-center" onClick={e => e.stopPropagation()}>
                                {gallery.map((img, i) => (
                                    <button
                                        key={i}
                                        onClick={() => setActiveGalleryIdx(i)}
                                        className={clsx(
                                            'shrink-0 w-16 h-12 rounded-lg overflow-hidden border-2 transition-all',
                                            i === activeGalleryIdx ? 'opacity-100' : 'border-transparent opacity-40 hover:opacity-80'
                                        )}
                                        style={i === activeGalleryIdx ? { borderColor: 'var(--accent-500)' } : {}}
                                    >
                                        <img src={img} alt="" className="w-full h-full object-cover" />
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>,
                document.body
            )}

            {/* Dialogs are rendered via createPortal into document.body to
                escape this page's .animate-page-entry wrapper. The wrapper
                leaves transform: translateY(0) which makes it the containing
                block for fixed descendants, so a plain `fixed inset-0` dialog
                gets reframed to the wrapper's full scroll height and lands
                off-screen — the user sees the black overlay and has to scroll
                to find the actual content. */}
            <ShareOptionsDialog
                isOpen={showShareDialog}
                onClose={() => setShowShareDialog(false)}
                onSelect={handleShareAction}
            />

            {showReportDialog && typeof document !== 'undefined' && createPortal(
                <ReportInfractionDialog
                    alreadyReported={hasReported}
                    onCancel={() => setShowReportDialog(false)}
                    onSubmit={handleSubmitReport}
                />,
                document.body
            )}
        </div >
    );
};

interface ReportInfractionDialogProps {
    alreadyReported: boolean;
    onCancel: () => void;
    onSubmit: (reason: 'watermark' | 'contact_info' | 'other', details: string) => void;
}

const REPORT_REASONS: { value: 'watermark' | 'contact_info' | 'other'; label: string; hint: string }[] = [
    { value: 'watermark',    label: 'Sello de agua en fotos',  hint: 'Logos, marcas o textos sobre las fotos.' },
    { value: 'contact_info', label: 'Datos de contacto',        hint: 'Teléfonos, correos o nombres en fotos o descripción.' },
    { value: 'other',        label: 'Otro',                     hint: 'Describe el problema en el cuadro de abajo.' },
];

const ReportInfractionDialog: React.FC<ReportInfractionDialogProps> = ({ alreadyReported, onCancel, onSubmit }) => {
    const [reason, setReason] = useState<'watermark' | 'contact_info' | 'other'>('watermark');
    const [details, setDetails] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const detailsRequired = reason === 'other';
    const detailsTooLong = details.length > 500;
    const canSubmit = !submitting && !detailsTooLong && (!detailsRequired || details.trim().length > 0);

    const handleSubmit = async () => {
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await onSubmit(reason, details);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4" onClick={onCancel}>
            <div
                className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start gap-3 mb-4">
                    <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-rose-50 text-rose-600 shrink-0">
                        <Flag size={18} />
                    </div>
                    <div>
                        <h3 className="text-lg font-black text-slate-900">Reportar publicación</h3>
                        <p className="text-xs text-slate-500 mt-0.5">
                            {alreadyReported
                                ? 'Ya enviaste un reporte sobre esta propiedad. Si lo actualizas, sustituirá al anterior.'
                                : 'Selecciona la razón y, si quieres, agrega detalles. El equipo de Intercambio lo revisará.'}
                        </p>
                    </div>
                </div>
                <div className="flex flex-col gap-2 mb-4">
                    {REPORT_REASONS.map(r => (
                        <label
                            key={r.value}
                            className={clsx(
                                'flex items-start gap-3 p-3 rounded-xl border cursor-pointer transition-colors',
                                reason === r.value
                                    ? 'border-rose-300 bg-rose-50'
                                    : 'border-slate-200 hover:bg-slate-50'
                            )}
                        >
                            <input
                                type="radio"
                                name="report-reason"
                                value={r.value}
                                checked={reason === r.value}
                                onChange={() => setReason(r.value)}
                                className="mt-1 accent-rose-600"
                            />
                            <div>
                                <p className="text-sm font-bold text-slate-900">{r.label}</p>
                                <p className="text-[11px] text-slate-500 mt-0.5">{r.hint}</p>
                            </div>
                        </label>
                    ))}
                </div>
                <div className="mb-4">
                    <label className="block text-xs font-bold text-slate-700 mb-1">
                        Detalles {detailsRequired && <span className="text-rose-600">*</span>}
                    </label>
                    <textarea
                        value={details}
                        onChange={e => setDetails(e.target.value)}
                        rows={3}
                        maxLength={600}
                        placeholder={detailsRequired ? 'Describe el problema (obligatorio)' : 'Opcional — agrega contexto si quieres'}
                        className={clsx(
                            'w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2 focus:ring-rose-200',
                            detailsTooLong ? 'border-rose-400' : 'border-slate-200'
                        )}
                    />
                    <p className={clsx('text-[10px] mt-1', detailsTooLong ? 'text-rose-600' : 'text-slate-400')}>
                        {details.length} / 500
                    </p>
                </div>
                <div className="flex flex-col gap-2">
                    <button
                        onClick={handleSubmit}
                        disabled={!canSubmit}
                        className="w-full py-3 rounded-xl bg-rose-600 hover:bg-rose-500 text-white font-bold transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
                    >
                        {submitting ? <Loader2 size={16} className="animate-spin" /> : <Flag size={14} />}
                        {alreadyReported ? 'Actualizar reporte' : 'Enviar reporte'}
                    </button>
                    <button
                        onClick={onCancel}
                        className="w-full py-2 text-sm font-semibold text-slate-500 hover:text-slate-700 transition-colors"
                    >
                        Cancelar
                    </button>
                </div>
            </div>
        </div>
    );
};
